// frontend/src/pages/Home.jsx
import React, { lazy, Suspense, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GuideWelcome from "../components/GuideWelcome";
import "./Home.css";
import "./HomeV2.css";
import { Helmet } from "react-helmet-async";
import { AIContext } from "../context/AIContext";

// Lazy-load AIWidget
const AIWidget = lazy(() => import("../components/AIWidget"));

const SITE_ORIGIN = "https://www.kirunatours.com";

export default function Home() {
  const { isAIOpen, setIsAIOpen } = useContext(AIContext);
  const [featured, setFeatured] = useState([]);
  const [abiskoImage, setAbiskoImage] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    fetch('/__tours.json', { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error('Tour data unavailable'); return r.json(); })
      .then(raw => {
        const list = Array.isArray(raw.data) ? raw.data : [];
        const abiskoTour = list.find(t => String(t.slug || "").includes("abisko") && (t.image_url || t.hero_image || t.cover_image));
        setAbiskoImage(abiskoTour ? (abiskoTour.image_url || abiskoTour.hero_image || abiskoTour.cover_image) : "");
        const desired = ["northern-lights-car-chase-kiruna", "drive-your-own-dog-sled-team", "snowmobile-adventure-kiruna-2-hour-arctic-wilderness-tour", "arctic-snowshoe-moments-fire-cocoa-marshmallows", "moose-spotting-tour-from-kiruna-to-nikkaluokta", "kiruna-arctic-river-fire-experience-premium-outdoor-dinner"];
        const chosen = desired.map(s => list.find(t => t.slug === s)).filter(Boolean);
        const used = new Set(chosen.map(t => t.slug));
        for (const t of list) {
          if (chosen.length >= 6) break;
          if (!used.has(t.slug) && (t.image_url || t.hero_image || t.cover_image)) {
            chosen.push(t); used.add(t.slug);
          }
        }
        setFeatured(chosen.slice(0, 6));
      })
      .catch(err => { if (err.name !== 'AbortError') console.warn('Homepage tour cards:', err.message); });
    return () => controller.abort();
  }, []);

  const title = "Northern Lights Tours Kiruna | Aurora Viewing & Arctic Adventures";
  const desc = "Experience Northern Lights tours in Kiruna with local guides. Aurora viewing, dog sledding & Arctic adventures. Book your Northern Lights experience today!";

  return (
    <main className="home-container">
      <Helmet>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content="Northern Lights tours, dog sledding, reindeer sledding with local guides in Kiruna and Abisko." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_ORIGIN}/`} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_ORIGIN}/`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            name: "Kiruna Tours",
            description: "Local tour company in Kiruna since 2005. Authentic Arctic experiences with expert guides.",
            url: SITE_ORIGIN,
            address: {
              "@type": "PostalAddress",
              streetAddress: "Poikkijärvivägen 55",
              addressLocality: "Kiruna",
              addressRegion: "Norrbotten",
              postalCode: "981 92",
              addressCountry: "SE",
            },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "09:00",
              closes: "18:00",
            },
            telephone: "+46706866709",
            tourBookingPage: `${SITE_ORIGIN}/tours/`
          })}
        </script>
      </Helmet>

      <section className="kt-home-hero" aria-labelledby="kt-home-heading">
        <div className="kt-home-hero-copy">
          <span className="kt-home-eyebrow">KIRUNA &amp; ABISKO · SWEDISH LAPLAND</span>
          <h1 id="kt-home-heading">Northern Lights &amp; Arctic Adventures in Kiruna</h1>
          <p>Discover Northern Lights tours, husky sledding, snowmobile adventures and unforgettable winter experiences with local guides and selected partners.</p>
          <div className="kt-home-hero-actions"><Link className="kt-home-action" to="/tours/">Explore all tours</Link><Link className="kt-home-action kt-home-action-light" to="/tours/northern-lights/">Northern Lights tours</Link></div>
          <div className="kt-home-trust">Local knowledge <span>·</span> Handpicked Arctic experiences <span>·</span> Secure booking</div>
        </div>
      </section>
      <section className="kt-home-featured" aria-labelledby="kt-home-featured-title">
        <div className="kt-home-section-heading"><div><span className="kt-home-kicker">CHOOSE YOUR EXPERIENCE</span><h2 id="kt-home-featured-title">Popular Arctic tours</h2><p>Explore real experiences with up-to-date tour information and prices.</p></div><Link to="/tours/">See all tours →</Link></div>
        <div className="kt-home-tour-grid">{featured.map(t => {
          const url = `/tours/${encodeURIComponent(t.slug)}/`;
          const img = t.image_url || t.hero_image || t.cover_image;
          const price = Number(t.price);
          const priceLabel = Number.isFinite(price) && price > 0 ? `From ${new Intl.NumberFormat('en-US').format(price)} SEK` : 'View tour details';
          return <article className="kt-home-tour-card" key={t.slug}>
            <Link to={url} className="kt-home-tour-image-link" aria-label={`Explore ${t.title}`}>{img ? <img src={img.startsWith('http') ? img : `${SITE_ORIGIN}${img.startsWith('/') ? '' : '/'}${img}`} alt={t.title} width="640" height="410" loading="lazy" decoding="async"/> : <div className="kt-home-image-placeholder" aria-hidden="true"/>}</Link>
            <div className="kt-home-tour-content"><h3><Link to={url}>{t.title}</Link></h3><div className="kt-home-tour-bottom"><span>{priceLabel}</span><Link to={url}>View tour →</Link></div></div>
          </article>;
        })}</div>
      </section>
      <section className="kt-home-categories" aria-labelledby="kt-home-categories-heading"><div className="kt-home-section-heading"><div><span className="kt-home-kicker">EXPLORE BY ACTIVITY</span><h2 id="kt-home-categories-heading">What would you like to experience?</h2></div></div><div className="kt-home-category-grid"><Link to="/tours/northern-lights/">✦ <span>Northern Lights</span> →</Link><Link to="/tours/dog-sledding/">🐾 <span>Dog Sledding</span> →</Link><Link to="/tours/snowmobile/">❄ <span>Snowmobile Tours</span> →</Link><Link to="/tours/reindeer/">♢ <span>Reindeer Experiences</span> →</Link></div></section>

      <GuideWelcome />

      <section className="kt-home-v21-intro" aria-labelledby="kt-v21-intro-title">
        <div><span className="kt-home-kicker">LOCAL ARCTIC EXPERIENCES</span><h2 id="kt-v21-intro-title">Explore Swedish Lapland with confidence</h2>
          <p>From aurora evenings to snow-covered wilderness, discover tours operated by local guides and selected partners. Group sizes, pickup locations and inclusions are listed on each tour page.</p></div>
        <div className="kt-home-v21-intro-links" aria-label="More experiences"><Link to="/tours/northern-lights-car-chase-kiruna/">Aurora tours ↗</Link><Link to="/tours/drive-your-own-dog-sled-team/">Dog sledding ↗</Link><Link to="/tours/snowmobile-adventure-kiruna-2-hour-arctic-wilderness-tour/">Snowmobile trips ↗</Link><Link to="/tours/reindeer-sledding-day-tour-in-kiruna/">Reindeer experiences ↗</Link><a href="https://www.aurorarivercamp.com" target="_blank" rel="noopener noreferrer">Glass igloo stays ↗</a></div>
      </section>

      <section className="destination-section kt-home-v21-destinations">
            <h2>Explore Two Arctic Paradises</h2>
            <div className="destination-cards">
                  <div className="destination-card" style={(() => { const t = featured.find(t => t.slug === "moose-spotting-tour-from-kiruna-to-nikkaluokta"); const img = t?.image_url || t?.hero_image || t?.cover_image; return img ? { "--kt-destination-image": `url("${img}")` } : {}; })()}>
                        <h3>Kiruna Adventures</h3>
                        <ul>
                              <li>Gateway to Swedish Lapland</li>
                              <li>Historic Icehotel visits</li>
                              <li><Link to="/tours/moose-spotting-tour-from-kiruna-to-nikkaluokta/" className="text-link">Moose safaris in winter wilderness</Link></li>
                              <li>Traditional Sami culture experiences</li>
                              {/* Nya länkar till orphan tours */}
                              <li><Link to="/tours/daytime-snowshoe-hike-kiruna/" className="text-link">Snowshoe Hiking Tours</Link></li>
                              <li><Link to="/tours/arctic-snowshoe-moments-fire-cocoa-marshmallows/" className="text-link">Arctic Snowshoe Moments</Link></li>
                              <li><Link to="/tours/narvik-fjord-and-narvik-city-sightseeing/" className="text-link">Fjord Sightseeing</Link></li>
                        </ul>
                        <Link to="/tours/" className="text-link">Discover Kiruna tours →</Link>
                  </div>
                  <div className="destination-card" style={abiskoImage ? { "--kt-destination-image": `url("${abiskoImage}")` } : {}}>
                        <h3>Abisko Experiences</h3>
                        <ul>
                              <li>Europe's best Northern Lights viewing</li>
                              <li>Famous "Blue Hole" microclimate</li>
                              <li>Snowshoe hiking in national park</li>
                              <li>Aurora Sky Station access</li>
                              {/* Nya länkar till orphan tours */}
                              <li><Link to="/tours/tour-to-narvik-from-abisko/" className="text-link">Tour to Narvik from Abisko</Link></li>
                              <li><Link to="/tours/morning-hike-in-abisko-national-park/" className="text-link">Morning Hikes</Link></li>
                        </ul>
                        <Link to="/tours/" className="text-link">Explore Abisko tours →</Link>
                  </div>
            </div>
      </section>

      <section className="benefits-section kt-home-v21-benefits">
        <h2>Why Choose Kiruna Tours?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h3>Local Expertise</h3>
            <p>Our guides know every secret spot for Aurora viewing and wildlife encounters.</p>
          </div>
          <div className="benefit-card">
            <h3>Small Groups</h3>
            <p>Small-group experiences available with selected operators for personalized attention and thoughtful environmental impact.</p>
          </div>
          <div className="benefit-card">
            <h3>Eco-Conscious</h3>
            <p>We partner with sustainable operators and follow Leave No Trace principles.</p>
          </div>
        </div>
      </section>

      <section className="testimonial-section kt-home-v21-testimonial">
        <h2>What Our Guests Say</h2>
        <blockquote>
          "The Northern Lights tour with Kiruna Tours exceeded all expectations! Our guide knew exactly where to go despite cloudy forecasts. Saw incredible Aurora displays three nights in a row."
          <cite>- Sarah K., February 2024</cite>
        </blockquote>
      </section>

      <section className="faq-preview kt-home-v21-faq">
        <h2>Common Questions</h2>
        <div className="faq-items">
          <div className="faq-item">
            <h3>When is the best time to see Northern Lights?</h3>
            <p>September through April, with peak visibility from December to March.</p>
          </div>
          <div className="faq-item">
            <h3>How cold does it get in winter?</h3>
            <p>Winter temperatures can be well below freezing. Check each tour’s inclusions for thermal clothing and equipment.</p>
          </div>
        </div>
        <Link to="/faq/" className="cta-button">View Full FAQ</Link>
      </section>

      <Suspense fallback={null}>
        {isAIOpen && <AIWidget visible={true} onClose={() => setIsAIOpen(false)} />}
      </Suspense>
    
<section className="kt-home-v21-forecast">
  <h2>Northern Lights Forecast in Kiruna</h2>
  <p>
    Planning to see Aurora tonight? Read our complete{" "}
    <Link to="/travel-guide/northern-lights-forecast-explained/">
      Northern Lights Forecast Guide
    </Link>{" "}
    with KP index explanation, best months and viewing locations.
  </p>
</section>


<section className="kt-home-v21-guides">
  <h2>Popular Kiruna Travel Guides</h2>
  <ul>
    <li><Link to="/travel-guide/kiruna-winter-itinerary/">Top Things to Do in Kiruna</Link></li>
    <li><Link to="/travel-guide/swedish-lapland-winter-travel-guide/">Kiruna Travel Guide</Link></li>
    <li><Link to="/travel-guide/snowmobile-tours-in-kiruna-guide/">Snowmobile Tours in Kiruna</Link></li>
    <li><Link to="/travel-guide/kiruna-sami-reindeer-experiences/">Reindeer Experience in Kiruna</Link></li>
    <li><Link to="/travel-guide/kiruna-aurora-river-camp-unique-stays/">Glass Igloo Stay in Kiruna</Link></li>
    <li><a href="/tours/day-trip-to-abisko/">Day Trip to Abisko</a></li>
    <li><a href="/tours/visit-the-world-famous-icehotel/">Visit the ICEHOTEL</a></li>
    <li><a href="/tours/ice-climbing-tour-in-abisko-beginner-friendly/">Ice Climbing in Abisko</a></li>
  </ul>
</section>

</main>
  );
}